import * as React from 'react'
import { Localized } from '@fluent/react'
import Conversation, { ConversationData, ConversationMessage } from '../../../../api/conversation'
import {
  DateSeparator,
  HISTORY_LIMIT,
  LoadingMessages,
  StateChanged,
  UserJoined,
  UserMessages,
} from '../Messages'
import store from '../../../../store'
import { addMessages, AddMessagesData } from '../../../../store/actions/conversations'

interface MessagesStreamProps {
  conversation: Conversation
  convData: ConversationData
  messages: ConversationMessage[]
}

interface MessageStreamState {
  historyHasEnded: boolean
  loadingMessagesInView: Set<string>
  selectedMsg: number
}


class MessagesStream extends React.Component<MessagesStreamProps> {
  state: MessageStreamState = {
    historyHasEnded: false,
    loadingMessagesInView: new Set(),
    selectedMsg: 0,
  }

  async componentDidMount() {
    const { conversation, convData } = this.props
    const chat = this.chatContainer.current!

    conversation.addEventListener('newmessage', this.scrollToBottom)
    conversation.addEventListener('statechanged', this.scrollToBottom)
    conversation.addEventListener('userjoined', this.scrollToBottom)
    chat.addEventListener('scroll', this.handleScroll)

    const hash = Number(window.location.hash.replace('#', ''))
    if (hash) {
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState({ selectedMsg: hash })
      if (convData.knownMessages.has(hash)) {
        document.getElementById(`msg-${hash}`)!.scrollIntoView()
        chat.scrollBy(0, -(chat.offsetHeight / 2))
      } else {
        try {
          const {
            before,
            after,
          } = await conversation.getHistory(hash, HISTORY_LIMIT, HISTORY_LIMIT)
          const ref = after[0]

          if (!before.length && !after.length) {
            throw new Error()
          }

          const data: AddMessagesData = {
            messages: [...before, ...after].reverse(),
            conversationId: conversation.id,
            position: 'start',
            addLoadingMsgBefore: !(before.length < HISTORY_LIMIT),
            addLoadingMsgAfter: !(after.length < HISTORY_LIMIT),
          }

          store.dispatch(addMessages(data))

          const refMsg = document.getElementById(`msg-${ref.id}`)
          if (refMsg) {
            refMsg.scrollIntoView()
            chat.scrollBy(0, -(chat.offsetHeight / 2))
          }
        } catch (e) {
          console.error(`Couldn't find ref message with id: ${hash} for conversation with id: ${conversation.id}`)
        }
      }
    } else {
      chat.scrollTo({ top: chat.scrollHeight })
    }

    this.checkForLoadingMessagesInView()
  }

  componentWillUnmount() {
    this.props.conversation.removeEventListener('newmessage', this.scrollToBottom)
    this.props.conversation.removeEventListener('statechanged', this.scrollToBottom)
    this.props.conversation.removeEventListener('userjoined', this.scrollToBottom)
    this.chatContainer.current!.removeEventListener('scroll', this.handleScroll)
  }

  chatContainer: React.RefObject<HTMLDivElement> = React.createRef()

  public render() {
    const rendered = []

    let i = 0
    for (const msg of this.props.messages) {
      if (msg.kind === 'message:user') {
        rendered.push(<UserMessages key={i} data={msg} selectedMsg={this.state.selectedMsg} />)
      } else if (msg.kind === 'message:separator') {
        rendered.push(<DateSeparator key={i} data={msg} />)
      } else if (msg.kind === 'message:loading') {
        rendered.push(
          <LoadingMessages
            key={i}
            data={msg}
            isInView={this.state.loadingMessagesInView.has(msg.id)}
            conversation={this.props.conversation}
            chatContainer={this.chatContainer.current!}
          />,
        )
      } else if (msg.kind === 'message:userjoined') {
        rendered.push(<UserJoined key={i} data={msg} />)
      } else if (msg.kind === 'message:statechanged' && msg.flags.length) {
        rendered.push(<StateChanged key={i} data={msg} />)
      }
      i++
    }

    return (
      <div className="chat__messages" ref={this.chatContainer}>
        {
          this.state.historyHasEnded ?
            <div className="chat__msg chat__msg--info">
              <Localized id="chat-message-begining">
                This is very beggining of your message history.
              </Localized>
            </div>
            : null
        }
        {rendered}
      </div>
    )
  }

  /**
   * Scroll to the bottom of conversation when new message is received.
   */
  public scrollToBottom = () => {
    const chat = this.chatContainer.current!
    const scrollBy = chat.scrollHeight - chat.clientHeight - chat.scrollTop

    const lastMsg = chat.querySelector<HTMLDivElement>(
      // eslint-disable-next-line max-len
      '.chat__msg:last-child .chat__msg-single:last-child, .chat__msg--state-changed:last-child, .chat__msg--user-joined:last-child')
    const lastMsgHeight = lastMsg ? lastMsg.offsetHeight : 0

    // Scroll only if user didn't scroll to the top manually for value more than 150px.
    if ((scrollBy - lastMsgHeight) < 150) {
      this.chatContainer.current!.scrollBy(0, scrollBy)
    }
  }

  timeout: NodeJS.Timeout | null = null

  private handleScroll = () => {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
    this.timeout = setTimeout(() => {
      // User stopped scrolling
      this.checkForLoadingMessagesInView()
    }, 200)
  }

  public checkForLoadingMessagesInView = () => {
    const chat = this.chatContainer.current!
    const loadingMessages = chat.querySelectorAll(
      '.chat__msg--loading') as NodeListOf<HTMLDivElement>
    if (loadingMessages.length === 0) return
    const loadingMessagesInView = new Set<string>()
    // Load messages only from one LoadingMessages at the time.
    Array.from(loadingMessages).some(msg => {
      if (this.isElemInChatView(msg)) {
        loadingMessagesInView.add(msg.dataset.id!)
        return true
      }
      return false
    })
    this.setState({ loadingMessagesInView })
  }

  private isElemInChatView = (el: Element) => {
    const chatRect = this.chatContainer.current!.getBoundingClientRect()
    const elRect = el.getBoundingClientRect()
    const top = elRect.top
    const height = elRect.height

    if (!(top <= chatRect.bottom)) return false
    // Check if the element is out of view due to a container scrolling
    if ((top + height) <= chatRect.top) return false
    // Check its within the document viewport
    return top <= document.documentElement.clientHeight
  }
}

export default MessagesStream
